import { AlertModal, Button, Checkbox, QuickFilterButton } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { CellProps, Column } from "react-table";

import style from "./file-sharing.scss";
import FilenameCell from "./FilenameCell";
import FileSharingErrorDialog from "./FileSharingErrorDialog";
import { FilterType } from "./FileSharingView";
import { buildFileTree, FileTreeNodeSortKey, formatBytes } from "./fileTreeUtils";
import { useFileSharingDrop } from "./useFileSharingDrop";
import DownArrow from "components/icons/DownArrow";
import UpArrow from "components/icons/UpArrow";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import DateCell from "components/table/DateCell";
import Table, { createSortByHeaderSuffix, deriveColumnWidth, SortState } from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import { AUTH_FILESHARE_EDIT } from "domain/authority";
import { FileTreeNode, hasHttpStatus, SharedFile } from "domain/fileSharing";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import layoutStyle from "styles/layout.scss";
import { RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    files: SharedFile[];
    existingPaths: string[];
    loaded: boolean;
    failureMessage?: string;
    filter: FilterType;
    onFilterChange: (filter: FilterType) => void;
    cursor?: string;
    onLoadMore: () => void;
    loadingMore: boolean;
    onRefresh: () => void;
    onRefreshQuota: () => void;
    sortCallbacks: { onChange: (column: string, ascending: boolean) => void; onDisable: () => void };
    sortColumn?: FileTreeNodeSortKey;
    sortAscending: boolean;
}

interface SortableHeaderProps extends SortState {
    text: string;
    identifier: FileTreeNodeSortKey;
}

const SortableHeader = ({ text, identifier, ...sortState }: SortableHeaderProps): JSX.Element => {
    const theme = useTheme();
    const suffix = createSortByHeaderSuffix(sortState as SortState, identifier);
    return (
        <TextWithTooltip text={text}>
            {text}
            <button
                className={classNames(style.tableSortButton, {
                    [style.sortNone]: suffix === "",
                    [style.sortAscending]: suffix === "ascending",
                    [style.sortDescending]: suffix === "descending",
                })}
            >
                <DownArrow
                    className={style.downArrow}
                    color={suffix === "descending" ? theme.sortIconActiveColor : theme.sortIconRegularColor}
                />
                <UpArrow
                    className={style.upArrow}
                    color={suffix === "ascending" ? theme.sortIconActiveColor : theme.sortIconRegularColor}
                />
            </button>
        </TextWithTooltip>
    );
};

const createSortableHeader = (text: string, identifier: FileTreeNodeSortKey): React.FC => {
    return (sortState: SortState) => <SortableHeader text={text} identifier={identifier} {...sortState} />;
};

const FileSharingTable = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [dropError, setDropError] = React.useState<string | undefined>();
    const [moveLoading, setMoveLoading] = React.useState(false);
    const [allowOverride, setAllowOverride] = React.useState(false);

    const handleDropError = React.useCallback(
        (error: Error) => {
            const status = hasHttpStatus(error) ? error.status : undefined;
            setDropError(
                status === 400
                    ? t("FileSharing.move.dragDropErrorConflict", {
                          allowOverride: t("FileSharing.move.allowOverride"),
                      })
                    : status === 404
                      ? t("FileSharing.move.errorNotFound")
                      : t("Common.resultErrorMessage")
            );
        },
        [t]
    );

    const { pendingDrop, setPendingDrop, onDrop } = useFileSharingDrop(props.files, props.onRefresh, handleDropError);
    const canEdit = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_EDIT]);

    React.useEffect(() => {
        if (pendingDrop) {
            setAllowOverride(false);
        }
    }, [pendingDrop]);

    // Updated every render so the frozen Cell closure always calls the current callback/value.
    const onRefreshRef = React.useRef(props.onRefresh);
    onRefreshRef.current = props.onRefresh;
    const onRefreshQuotaRef = React.useRef(props.onRefreshQuota);
    onRefreshQuotaRef.current = props.onRefreshQuota;
    const existingPathsRef = React.useRef<string[]>(props.existingPaths);
    existingPathsRef.current = props.existingPaths;

    const [containerWidthPixels, setContainerWidthPixels] = React.useState(0);

    React.useEffect(() => {
        const tableContainer = tableContainerRef.current;
        if (!tableContainer || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidthPixels(entry.contentRect.width);
        });
        observer.observe(tableContainer);
        return () => observer.disconnect();
    }, []);

    const fileTree = React.useMemo(
        () => buildFileTree(props.files, props.sortColumn, props.sortAscending),
        [props.files, props.sortColumn, props.sortAscending]
    );

    const columns: Array<Column<FileTreeNode>> = React.useMemo(
        () => [
            {
                Header: createSortableHeader(t("FileSharing.columns.name"), "filename"),
                accessor: "filename",
                Cell: (cellInfo: CellProps<FileTreeNode>) => (
                    <FilenameCell
                        row={cellInfo.row}
                        existingPaths={existingPathsRef.current}
                        onDrop={canEdit ? onDrop : undefined}
                        onRefresh={() => onRefreshRef.current()}
                        onRefreshQuota={() => onRefreshQuotaRef.current()}
                    />
                ),
                width: deriveColumnWidth(46, tableContainerRef),
            },
            {
                Header: createSortableHeader(t("FileSharing.columns.size"), "fileSize"),
                accessor: "fileSize",
                Cell: ({ cell: { value } }: CellProps<FileTreeNode, number>) => <span>{formatBytes(value)}</span>,
                width: deriveColumnWidth(14, tableContainerRef),
            },
            {
                Header: createSortableHeader(t("FileSharing.columns.uploadedAt"), "creationDate"),
                accessor: "creationDate",
                Cell: ({ row }: CellProps<FileTreeNode>) =>
                    row.original.isFolder ? (
                        <span>{"—"}</span>
                    ) : (
                        <DateCell tooltip={true} value={row.original.creationDate} />
                    ),
                width: deriveColumnWidth(15, tableContainerRef),
            },
            {
                Header: createSortableHeader(t("FileSharing.columns.uploader"), "uploader"),
                accessor: "uploader",
                Cell: ({ row }: CellProps<FileTreeNode>) =>
                    row.original.isFolder ? (
                        <span>{"—"}</span>
                    ) : (
                        <TextWithTooltip text={row.original.uploader ?? "—"} />
                    ),
                width: deriveColumnWidth(15, tableContainerRef),
            },
            {
                Header: createSortableHeader(t("FileSharing.columns.editedBy"), "editedBy"),
                accessor: "editedBy",
                Cell: ({ row }: CellProps<FileTreeNode>) =>
                    !row.original.isFolder && <TextWithTooltip text={row.original.editedBy ?? ""} />,
                width: deriveColumnWidth(15, tableContainerRef),
            },
            {
                Header: createSortableHeader(t("FileSharing.columns.editedDate"), "editedDate"),
                accessor: "editedDate",
                Cell: ({ row }: CellProps<FileTreeNode>) =>
                    row.original.editedDate && <DateCell tooltip={true} value={row.original.editedDate} />,
                width: deriveColumnWidth(15, tableContainerRef),
            },
        ],
        [t, onDrop, canEdit, containerWidthPixels]
    );

    return (
        <>
            {dropError && (
                <FileSharingErrorDialog
                    error={dropError}
                    headingText={t("FileSharing.move.dragDropErrorTitle")}
                    onClose={() => setDropError(undefined)}
                />
            )}
            {pendingDrop && (
                <AlertModal
                    title={t("FileSharing.move.confirmTitle", { name: pendingDrop.name })}
                    variant="Warning"
                    isOpen={true}
                    hideModal={() => {
                        if (!moveLoading) {
                            setPendingDrop(undefined);
                        }
                    }}
                    buttons={
                        <>
                            <Button
                                variant="Secondary"
                                onClick={() => setPendingDrop(undefined)}
                                disabled={moveLoading}
                            >
                                {t("Common.cancel")}
                            </Button>
                            <Button
                                variant="Primary"
                                isLoading={moveLoading}
                                testId={testIds.common.dialog.primaryButton}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.FILE_SHARING,
                                        action: Action.MOVE_FILE,
                                    });
                                    setMoveLoading(true);
                                    pendingDrop.execute(allowOverride).finally(() => {
                                        setMoveLoading(false);
                                        setPendingDrop(undefined);
                                    });
                                }}
                            >
                                {t("FileSharing.move.move")}
                            </Button>
                        </>
                    }
                >
                    <span>{t("FileSharing.move.confirmBody", { from: pendingDrop.from, to: pendingDrop.to })}</span>
                    <Checkbox
                        label={t("FileSharing.move.allowOverride")}
                        name="drag-drop-allow-override"
                        checked={allowOverride}
                        hideLabelSection={true}
                        onChange={(e) => setAllowOverride(e.target.checked)}
                    />
                </AlertModal>
            )}
            <div className={style.filters} data-testid={testIds.common.primaryView.table.filter.itself}>
                <QuickFilterButton
                    isActive={props.filter === "all"}
                    onClick={() => props.onFilterChange("all")}
                    testId={testIds.common.primaryView.table.filter.button}
                    dataType="all"
                >
                    {t("FileSharing.filterAll")}
                </QuickFilterButton>
                <QuickFilterButton
                    isActive={props.filter === "mine"}
                    onClick={() => props.onFilterChange("mine")}
                    testId={testIds.common.primaryView.table.filter.button}
                    dataType="mine"
                >
                    {t("FileSharing.filterMine")}
                </QuickFilterButton>
            </div>
            {canEdit && (
                <div
                    className={style.rootDropZone}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        e.currentTarget.classList.add(style.dragOver);
                    }}
                    onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            e.currentTarget.classList.remove(style.dragOver);
                        }
                    }}
                    onDrop={(e) => onDrop("", e)}
                >
                    {t("FileSharing.dropToRoot")}
                </div>
            )}
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <Table
                    tableIdentity={RepositoryKey.FILE_SHARING_TABLE}
                    data={fileTree}
                    columns={columns}
                    loaded={props.loaded}
                    failureMessage={props.failureMessage}
                    emptyMessage={t("FileSharing.emptyRoot")}
                    sortCallbacks={props.sortCallbacks}
                    rowHover={true}
                    testId={testIds.workArea.fileSharing.table.itself}
                />
            </div>
            {props.cursor &&
                !props.failureMessage &&
                (props.loadingMore ? (
                    <LoadingIndicator small={true} />
                ) : (
                    <Button
                        variant="Secondary"
                        className={style.loadMoreButton}
                        testId={testIds.common.primaryView.table.loadMoreButton}
                        onClick={props.onLoadMore}
                    >
                        {t("Common.loadMore")}
                    </Button>
                ))}
        </>
    );
};

export default FileSharingTable;
